import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBlogPost } from '../api';

const CATEGORY_LABELS = { product: 'Product', education: 'Education' };

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getBlogPost(slug)
      .then((res) => { if (!cancelled) setPost(res.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px', color: 'var(--muted)' }}>Loading…</div>;
  }

  if (notFound || !post) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{ fontSize: 24, color: 'var(--text)' }}>Post not found</h1>
        <button
          onClick={() => navigate('/blog')}
          style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', padding: 0, fontSize: 14 }}
        >
          ← Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px' }}>
      <Helmet>
        <title>{post.title} — Algovex Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt || ''} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || ''} />
        <meta property="og:url" content={`https://algovex.ai/blog/${post.slug}`} />
        <link rel="canonical" href={`https://algovex.ai/blog/${post.slug}`} />
      </Helmet>

      <button
        onClick={() => navigate('/blog')}
        style={{ marginBottom: 20, background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', padding: 0, fontSize: 14 }}
      >
        ← Back to Blog
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
          color: post.category === 'product' ? 'var(--blue)' : 'var(--teal)',
        }}>
          {CATEGORY_LABELS[post.category] || post.category}
        </span>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(post.published_at)}</span>
      </div>

      <h1 style={{ margin: '0 0 24px', fontSize: 30, fontWeight: 800, color: 'var(--text)', lineHeight: 1.25 }}>
        {post.title}
      </h1>

      <div className="blog-post-content" style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.75 }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
      </div>
    </div>
  );
}
